import { useEffect, useState, type ReactNode } from 'react';
import type { NavSection } from '../../shared/types';
import { Link, navigate, useLocation } from '../lib/router';
import { useMe } from '../lib/me';
import { api } from '../lib/api';
import { Icon } from './Icon';
import { Avatar, Badge, useAction } from './ui';
import { BannerBar } from './BannerBar';

export function Shell({ children }: { children: ReactNode }) {
  const { me, refresh, tx, preferences } = useMe();
  const { path } = useLocation();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState<null | 'main' | 'elevate'>(null);
  const { busy, run } = useAction();
  useEffect(() => { setOpen(false); setMenu(null); }, [path]);
  if (!me) return null;

  const toolId = path.startsWith('/t/') ? path.split('/')[2] : undefined;
  const activeSection = path.startsWith('/s/') ? path.split('/')[2]
    : toolId ? me.nav.find((s) => s.tools.some((t) => t.id === toolId))?.id : undefined;

  const top = me.nav.filter((s) => s.group === 'top');
  const divisions = me.nav.filter((s) => s.group === 'divisions');
  const management = me.nav.filter((s) => s.group === 'management');
  const elevated = me.elevation.active;
  const elevatedNames = me.elevation.eligible.filter((r) => elevated.includes(r.id)).map((r) => r.name);

  const signOut = async () => { await api.post('/api/auth/logout'); location.href = '/login'; };
  const elevate = (roleId: string, on: boolean) => run(roleId, async () => {
    await api.post(on ? '/api/auth/elevate' : '/api/auth/elevate/revoke', { roleId });
    await refresh();
    setMenu(null);
  }, on ? 'Role elevated for this session' : 'Elevation revoked');

  /**
   * The Management group: each tool of a "direct" section as its own link, with dashboard
   * sections (Rules & Documentation, Management Tools) placed after the tool they name in navAfter.
   */
  const managementNav = (sections: NavSection[]) => {
    type Item = { kind: 'tool'; s: NavSection; t: NavSection['tools'][number] } | { kind: 'section'; s: NavSection };
    const items: Item[] = sections.filter((s) => s.layout !== 'dashboard').flatMap((s) => s.tools.map((t) => ({ kind: 'tool' as const, s, t })));
    for (const s of sections.filter((x) => x.layout === 'dashboard')) {
      const at = items.findIndex((i) => i.kind === 'tool' && i.t.id === s.navAfter);
      if (at >= 0) items.splice(at + 1, 0, { kind: 'section', s }); else items.push({ kind: 'section', s });
    }
    return items;
  };

  /** Tools of a "direct" section, each as its own top-level link. */
  const directLinks = (sections: NavSection[]) => sections.flatMap((s) => s.tools).map((t) => (
    <Link key={t.id} to={`/t/${t.id}`} className={`nav-item ${toolId === t.id ? 'active' : ''}`}>
      <Icon name={t.icon ?? 'tool'} /> <span>{t.name}</span>{t.status === 'beta' && <Badge tone="amber">beta</Badge>}
    </Link>
  ));

  return (
    <div className={`shell ${open ? 'nav-open' : ''} ${elevated.length ? 'is-elevated' : ''}`}>
      <a href="#main" className="skip-link">Skip to content</a>
      <aside className="sidebar">
        <Link to="/" className="brand">
          <img src="/logo.png" alt="" width={44} height={44} />
          <div>
            <strong>RAVENBOUND</strong>
            <span>{tx('Org Dashboard')}</span>
          </div>
        </Link>
        <nav className="nav" aria-label="Main">
          <Link to="/" className={`nav-item ${path === '/' ? 'active' : ''}`}><Icon name="home" /> <span>{tx('Home')}</span></Link>
          {directLinks(top)}

          {divisions.length > 0 && <div className="nav-label">{tx('Divisions')}</div>}
          {divisions.map((s) => (
            <div key={s.id} className="nav-group">
              <Link to={`/s/${s.id}`} className={`nav-item ${activeSection === s.id ? 'active' : ''}`}>
                <Icon name={s.icon} /> <span>{s.name}</span>
                {s.tools.length > 0 && <em>{s.tools.length}</em>}
              </Link>
              {activeSection === s.id && s.tools.length > 0 && (
                <div className="nav-sub">
                  {(s.navTools ? s.tools.filter((t) => s.navTools!.includes(t.id)) : s.tools).map((t) => (
                    <Link key={t.id} to={`/t/${t.id}`} className={`nav-subitem ${toolId === t.id ? 'active' : ''}`} title={t.tagline}>
                      <span>{t.name}{t.tagline && <small>{t.tagline}</small>}</span>
                      {t.status === 'beta' && <Badge tone="amber">beta</Badge>}
                    </Link>
                  ))}
                  {s.navTools && s.tools.length > s.navTools.length && (
                    <Link to={`/s/${s.id}`} className="nav-subitem nav-more"><span>All {s.tools.length} {tx(s.short)} pages and tools →</span></Link>
                  )}
                </div>
              )}
            </div>
          ))}

          {management.length > 0 && <div className="nav-label">{tx('Management')}</div>}
          {managementNav(management).map((item) => (item.kind === 'section' ? (
            <Link key={item.s.id} to={`/s/${item.s.id}`} className={`nav-item ${activeSection === item.s.id ? 'active' : ''}`}>
              <Icon name={item.s.icon} /> <span>{tx(item.s.name)}</span>{item.s.tools.length > 0 && <em>{item.s.tools.length}</em>}
            </Link>
          ) : directLinks([{ ...item.s, tools: [item.t] }])))}
          {me.nav.length === 0 && <div className="nav-empty">Nothing unlocked yet. Your Discord roles decide what appears here.</div>}
        </nav>
        <div className="sidebar-foot">
          {elevated.length > 0 && (
            <div className="elevation-banner">
              <Icon name="elevate" size={14} /> <span>Elevated: {elevatedNames.join(', ')}</span>
              <button className="link-btn" disabled={busy !== null} onClick={() => run('all', async () => { await api.post('/api/auth/elevate/revoke', {}); await refresh(); }, 'Elevation revoked')}>Revoke</button>
            </div>
          )}
          <button className={`user-chip ${elevated.length ? 'elevated' : ''}`} onClick={() => setMenu((m) => (m ? null : 'main'))} aria-expanded={!!menu}>
            <Avatar src={me.user.avatarUrl} name={me.user.displayName} size={34} />
            <div><strong>{me.user.displayName}</strong><span>{elevatedNames[0] ?? (me.access.isAdmin ? 'Platform Admin' : me.roles.find((r) => r.ref.startsWith('discord:'))?.name ?? 'Member')}</span></div>
            <Icon name="down" size={14} />
          </button>
          {menu === 'main' && (
            <div className="user-menu">
              <Link to="/profile"><Icon name="citizen" size={16} /> {tx('Profile & sign-in')}</Link>
              <Link to="/appearance"><Icon name="eye" size={16} /> {tx('Appearance & accessibility')}</Link>
              {me.access.adminPanel && <Link to="/admin" className={path.startsWith('/admin') ? 'active' : ''}><Icon name="shield" size={16} /> {tx('Admin Panel')}</Link>}
              {me.elevation.eligible.length > 0 && (
                <button onClick={() => setMenu('elevate')}><Icon name="elevate" size={16} /> {tx('Elevate role')} <Icon name="chevron" size={14} className="push-right" /></button>
              )}
              <button onClick={signOut}><Icon name="logout" size={16} /> {tx('Sign out')}</button>
            </div>
          )}
          {menu === 'elevate' && (
            <div className="user-menu">
              <button className="menu-back" onClick={() => setMenu('main')}><Icon name="chevron" size={14} className="flip" /> Elevate role</button>
              <p className="menu-note">Elevation lasts until you revoke it or sign in again.</p>
              {me.elevation.eligible.map((r) => {
                const on = elevated.includes(r.id);
                return (
                  <button key={r.id} disabled={busy !== null} onClick={() => elevate(r.id, !on)} className={on ? 'on' : ''}>
                    <i className="role-dot" style={{ background: r.color }} /> {r.name}
                    <span className="push-right">{busy === r.id ? '…' : on ? 'Revoke' : 'Elevate'}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="version">v{me.platformVersion}{me.guildName ? ` · ${me.guildName}` : ''}</div>
        </div>
      </aside>
      <div className="scrim" onClick={() => setOpen(false)} />
      <div className="main">
        <header className="topbar">
          <button className="icon-btn menu-btn" onClick={() => setOpen(true)} aria-label="Open navigation"><Icon name="menu" /></button>
          <Link to="/" className="topbar-brand"><img src="/logo.png" alt="" width={28} height={28} /> RAVENBOUND</Link>
          <div className="topbar-spacer" />
          {elevated.length > 0 && <Badge tone="gold">Elevated</Badge>}
          <button className="icon-btn" onClick={() => navigate('/profile')} aria-label="Profile">
            <Avatar src={me.user.avatarUrl} name={me.user.displayName} size={28} />
          </button>
        </header>
        <BannerBar initial={me.banners ?? []} />
        {preferences.theme === 'emporium' && <EmporiumMarquee />}
        <main className="content" id="main" tabIndex={-1}>{children}</main>
      </div>
    </div>
  );
}

/** The novelty theme's sales banner. Decoration only. */
const MARQUEE = ['VERY GOOD DEALS', 'ALL ITEMS 0% OFF', 'NO REFUND', 'TRUSTED BY HUMANS (SOME)', 'MOST SHINY DASHBOARD IN STANTON', 'MEAT TUBE ACCEPTED', 'WIKELO EMPORIUM'];
function EmporiumMarquee() {
  const items = [...MARQUEE, ...MARQUEE];
  return (
    <div className="emp-marquee" aria-hidden="true">
      <div className="emp-marquee-track">{items.map((m, i) => <span key={i}>★ {m}</span>)}</div>
    </div>
  );
}
